// Debug page to check authentication status
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { signOut, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebaseConfig.js';

const DebugAuth = ({ onNavigate }) => {
  const { user, profile, loading } = useAuth();
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Authentication Debug</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Firebase Auth User</h2>
          <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto">
            {JSON.stringify(user, null, 2)}
          </pre>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Firestore Profile</h2>
          <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto">
            {JSON.stringify(profile, null, 2)}
          </pre>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Status Check</h2>
          <div className="space-y-2">
            <p><strong>Loading:</strong> {loading ? 'Yes' : 'No'}</p>
            <p><strong>User Type:</strong> {profile?.userType || 'None'}</p>
            <p><strong>Status:</strong> {profile?.status || 'None'}</p>
            <p><strong>Can Login:</strong> {
              profile?.status === 'approved' || profile?.status === 'pending_approval' ? 'Yes' : 'No'
            }</p>
          </div>
        </div>

        <div className="space-x-4">
          <button
            onClick={() => onNavigate('dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
          
          <button
            onClick={async () => {
              try {
                await signOut(auth);
                console.log('✅ Signed out successfully');
                alert('Signed out successfully');
              } catch (error) {
                console.error('❌ Sign out error:', error);
                alert('Sign out failed: ' + error.message);
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Sign Out
          </button>
          
                  <button
                    onClick={() => {
                      localStorage.clear();
                      console.log('✅ Local storage cleared');
                      alert('Local storage cleared');
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    Clear Local Storage
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        await signOut(auth);
                        localStorage.clear();
                        console.log('✅ Session cleared completely');
                        alert('Session cleared completely - please refresh the page');
                        window.location.reload();
                      } catch (error) {
                        console.error('❌ Clear session error:', error);
                        alert('Clear session failed: ' + error.message);
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Clear Session & Refresh
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        const newPassword = prompt('Enter new password for current user:');
                        if (newPassword && user) {
                          await updatePassword(user, newPassword);
                          console.log('✅ Password updated successfully');
                          alert('Password updated successfully! New password: ' + newPassword);
                        }
                      } catch (error) {
                        console.error('❌ Password update error:', error);
                        alert('Password update failed: ' + error.message);
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Reset Password
                  </button>

                  {/* New section for password reset */}
                  <div className="mt-8 pt-4 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Password Reset (for any user)</h3>
                    <input
                      type="email"
                      placeholder="Enter user's email to reset password"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm mb-2"
                    />
                    <button
                      onClick={async () => {
                        setResetMessage('');
                        if (!resetEmail) {
                          setResetMessage('Please enter an email address.');
                          return;
                        }
                        try {
                          await sendPasswordResetEmail(auth, resetEmail);
                          setResetMessage(`✅ Password reset email sent to ${resetEmail}. Check your inbox.`);
                          console.log('✅ Password reset email sent to:', resetEmail);
                        } catch (error) {
                          setResetMessage(`❌ Error sending reset email: ${error.message}`);
                          console.error('❌ Error sending reset email:', error);
                        }
                      }}
                      className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!resetEmail}
                    >
                      Send Password Reset Email
                    </button>
                    {resetMessage && (
                      <p className={`mt-2 text-sm ${resetMessage.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
                        {resetMessage}
                      </p>
                    )}
                  </div>
        </div>
      </div>
    </div>
  );
};

export default DebugAuth;
